import type { ReactNode } from "react";
import clsx from "clsx";
import PageSectionCard from "./PageSectionCard";

type PageFormSectionProps = {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  className?: string;
  headerClassName?: string;
  bodyClassName?: string;
  centered?: boolean;
  collapsible?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
  bodyId?: string;
};

const PageFormSection = ({
  eyebrow,
  title,
  description,
  children,
  className,
  headerClassName,
  bodyClassName,
  centered = false,
  collapsible = false,
  isOpen = true,
  onToggle,
  bodyId,
}: PageFormSectionProps) => {
  const toggleIcon = collapsible ? (
    <span
      className={clsx(
        "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-myteengig-border bg-myteengig-background text-myteengig-text-primary transition-transform",
        isOpen ? "rotate-180" : "",
      )}
      aria-hidden="true"
    >
      <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M5 7.5L10 12.5L15 7.5"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  ) : null;

  const content = centered && collapsible ? (
    <div className={clsx("w-full min-w-0 text-center", headerClassName)}>
      {eyebrow ? (
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-primary">{eyebrow}</p>
      ) : null}
      <div className="mt-2 flex items-start justify-center gap-3">
        <h2 className="min-w-0 flex-1 text-xl font-semibold text-myteengig-text-primary">{title}</h2>
        {toggleIcon}
      </div>
      {description ? <p className="mt-2 text-sm text-myteengig-text-muted">{description}</p> : null}
    </div>
  ) : (
    <div className={clsx("flex flex-col gap-2", centered && "w-full min-w-0 max-w-none items-stretch text-center", headerClassName)}>
      {eyebrow ? (
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-primary">{eyebrow}</p>
      ) : null}
      <h2 className="block w-full max-w-none text-xl font-semibold text-myteengig-text-primary">{title}</h2>
      {description ? <p className="block w-full max-w-none text-sm text-myteengig-text-muted">{description}</p> : null}
    </div>
  );

  return (
    <PageSectionCard className={clsx("p-5 sm:p-6", className)}>
      {collapsible ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-controls={bodyId}
          className={clsx(
            "w-full text-left",
            centered ? "block w-full text-center" : "flex items-start justify-between gap-4",
          )}
        >
          {centered ? (
            content
          ) : (
            <>
              {content}
              {toggleIcon}
            </>
          )}
        </button>
      ) : (
        <div>{content}</div>
      )}

      {isOpen ? (
        <div id={bodyId} className={clsx("mt-6", bodyClassName)}>
          {children}
        </div>
      ) : null}
    </PageSectionCard>
  );
};

export default PageFormSection;
